import { compactBynderImageProjection } from '@workspace/sanity-base/queries/projection';

export const computedInternalUrl = `
  select(
    defined(parentPage->parentPage->slug.current) => "/products/" + parentPage->parentPage->slug.current + "/" + parentPage->slug.current + "/" + slug.current,
    defined(parentPage->slug.current) => "/products/" + parentPage->slug.current + "/" + slug.current,
    _type == "productsList" => "/products/" + category->slug.current + "/" + slug.current,
    _type == "product" && defined(subcategory->parentCategory->slug.current) => "/products/" + subcategory->parentCategory->slug.current + "/" + subcategory->slug.current + "/" + slug.current,
    _type == "structaProductLevel1" => "/products/" + slug.current,
    _type == "articlesList" => "/" + select(
      type == "blogs" => "blog",
      type
    ),
    _type == "article" && defined(type) && type == "blogs" => "/blog/" + slug.current,
    _type == "article" && defined(type) => "/" + type + "/" + slug.current,
    _type == "documentPage" => "/documents/" + slug.current,
    _type == "faqs" => "/faq",
    _type == "contactUs" => "/contact-us",
    _type == "storeLocator" => "/store-locator",
    _type == "homePage" => "/",
    "/" + slug.current
  )
`;

export const internalReferenceUrl = `
  select(
    type == "internal" =>
      internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    url
  )
`;

export const directInternalReferenceUrl = `
  select(
    link.type == "internal" =>
      link.internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    link.type == "external" => link.url
  )
`;

export const bynderImageUrl = (field: string) => `
  coalesce(
    ${field}.selectedUrl,
    ${field}.url,
    ${field}.datUrl,
    ${field}.previewUrl,
    ${field}.previewImg
  )
`;

export const compatibleImageProjection = (
  alias: string = 'image',
  imageField: string = alias,
  bynderField: string = 'bynderImage',
  alt: string = `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${imageField}.alt, ${imageField}.asset->altText)`,
  title: string = `coalesce(${imageField}.title, ${imageField}.asset->title, ${imageField}.asset.title, ${bynderField}.name)`,
) => `
    "${alias}": select(
      defined(${imageField}) || defined(${bynderField}) => {
        "_type": coalesce(${imageField}._type, "image"),
        "asset": {
          "_id": coalesce(${bynderField}.id, ${imageField}.asset->_id, ${imageField}.asset._id),
          "_ref": ${imageField}.asset._ref,
          "_type": coalesce(${imageField}.asset->_type, ${imageField}.asset._type, "reference"),
          "url": coalesce(
            ${bynderField}.selectedUrl,
            ${bynderField}.url,
            ${bynderField}.datUrl,
            ${bynderField}.previewUrl,
            ${bynderField}.previewImg,
            ${imageField}.asset->url,
            ${imageField}.asset.url
          ),
          "metadata": {
            "lqip": ${imageField}.asset->metadata.lqip,
            "dimensions": {
              "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
              "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
              "aspectRatio": coalesce(${bynderField}.aspectRatio, ${imageField}.asset->metadata.dimensions.aspectRatio)
            }
          }
        },
        "crop": ${imageField}.crop,
        "hotspot": ${imageField}.hotspot,
        "alt": ${alt},
        "title": ${title},
        "url": coalesce(
          ${bynderField}.selectedUrl,
          ${bynderField}.url,
          ${bynderField}.datUrl,
          ${bynderField}.previewUrl,
          ${bynderField}.previewImg,
          ${imageField}.url,
          ${imageField}.asset->url,
          ${imageField}.asset.url
        ),
        "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
        "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
        "bynderImage": select(
          defined(${bynderField}) => {
            "_type": "bynder.asset",
            "id": ${bynderField}.id,
            "name": ${bynderField}.name,
            "description": ${bynderField}.description,
            "alt": coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt),
            "selectedUrl": ${bynderField}.selectedUrl,
            "url": coalesce(${bynderField}.url, ${bynderField}.selectedUrl, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
            "width": ${bynderField}.width,
            "height": ${bynderField}.height,
            "aspectRatio": ${bynderField}.aspectRatio
          },
          null
        )
      },
      null
    )
  `;

export const spreadCompatibleImageProjection = (
  imageField: string = 'image',
  bynderField: string = 'bynderImage',
  alt: string = `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${imageField}.alt, ${imageField}.asset->altText)`,
  title: string = `coalesce(${imageField}.title, ${imageField}.asset->title, ${imageField}.asset.title, ${bynderField}.name)`,
) => `
    ...select(
      defined(${imageField}) || defined(${bynderField}) => {
        "_type": coalesce(${imageField}._type, "image"),
        "asset": {
          "_id": coalesce(${bynderField}.id, ${imageField}.asset->_id, ${imageField}.asset._id),
          "_ref": ${imageField}.asset._ref,
          "_type": coalesce(${imageField}.asset->_type, ${imageField}.asset._type, "reference"),
          "url": coalesce(
            ${bynderField}.selectedUrl,
            ${bynderField}.url,
            ${bynderField}.datUrl,
            ${bynderField}.previewUrl,
            ${bynderField}.previewImg,
            ${imageField}.asset->url,
            ${imageField}.asset.url
          ),
          "metadata": {
            "lqip": ${imageField}.asset->metadata.lqip,
            "dimensions": {
              "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
              "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
              "aspectRatio": coalesce(${bynderField}.aspectRatio, ${imageField}.asset->metadata.dimensions.aspectRatio)
            }
          }
        },
        "crop": ${imageField}.crop,
        "hotspot": ${imageField}.hotspot,
        "alt": ${alt},
        "title": ${title},
        "url": coalesce(
          ${bynderField}.selectedUrl,
          ${bynderField}.url,
          ${bynderField}.datUrl,
          ${bynderField}.previewUrl,
          ${bynderField}.previewImg,
          ${imageField}.url,
          ${imageField}.asset->url,
          ${imageField}.asset.url
        ),
        "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
        "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
        "bynderImage": select(
          defined(${bynderField}) => {
            "_type": "bynder.asset",
            "id": ${bynderField}.id,
            "name": ${bynderField}.name,
            "description": ${bynderField}.description,
            "alt": coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt),
            "selectedUrl": ${bynderField}.selectedUrl,
            "url": coalesce(${bynderField}.url, ${bynderField}.selectedUrl, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
            "width": ${bynderField}.width,
            "height": ${bynderField}.height,
            "aspectRatio": ${bynderField}.aspectRatio
          },
          null
        )
      },
      null
    )
  `;

export const LOGO_PROJECTTION = `
  {
    ...logo,
    "bynderLogo": ${compactBynderImageProjection('logo.bynderLogo')},
    "link": logo.link{
      ...,
      "url": ${internalReferenceUrl},
    },
    "imageUrl": coalesce(${bynderImageUrl('logo.bynderLogo')}, logo.logo.asset->url),
  }
`;

export const imageProjection = `
  ...,
  "alt": coalesce(alt, asset->altText),
  "title": coalesce(title, asset->title),
  "url": asset->url,
`;
